import { useMemo, useState } from 'react';
import { Skull, Vote, Crosshair } from 'lucide-react';
import { useTranslation } from '@/i18n/LanguageContext';

/**
 * Stadium-shaped Mafia table — software-grade flat design.
 * Solid felt fill, sharp pinstripe, no radial gradients, no drop shadows.
 */

const VB_W = 200;
const VB_H = 130;
const FELT_X = 10;
const FELT_Y = 16;
const FELT_W = 180;
const FELT_H = 90;
const FELT_R = FELT_H / 2;

const CX = FELT_X + FELT_W / 2;
const CY = FELT_Y + FELT_H / 2;
const TX_HALF = (FELT_W - FELT_H) / 2;
const ARC_RAD = FELT_R;
const ARC_LEN = Math.PI * ARC_RAD;
const PERIMETER = 2 * (FELT_W - FELT_H) + 2 * ARC_LEN;

function pointAt(t) {
  let s = t;
  if (s < TX_HALF) return { x: CX + s, y: FELT_Y + FELT_H };
  s -= TX_HALF;
  if (s < ARC_LEN) {
    const theta = Math.PI / 2 - (s / ARC_LEN) * Math.PI;
    return { x: CX + TX_HALF + ARC_RAD * Math.cos(theta), y: CY + ARC_RAD * Math.sin(theta) };
  }
  s -= ARC_LEN;
  if (s < FELT_W - FELT_H) return { x: CX + TX_HALF - s, y: FELT_Y };
  s -= FELT_W - FELT_H;
  if (s < ARC_LEN) {
    const alpha = (s / ARC_LEN) * Math.PI;
    return { x: CX - TX_HALF - ARC_RAD * Math.sin(alpha), y: CY - ARC_RAD * Math.cos(alpha) };
  }
  s -= ARC_LEN;
  return { x: CX - TX_HALF + s, y: FELT_Y + FELT_H };
}

function seatPositions(n) {
  const gapLen = n <= 12 ? 36 : n <= 18 ? 28 : 22;
  const usable = PERIMETER - gapLen;
  const step = usable / n;
  const positions = [];
  for (let i = 0; i < n; i++) {
    const t = (gapLen / 2 + (i + 0.5) * step) % PERIMETER;
    positions.push(pointAt(t));
  }
  return positions;
}

const TEAM_BORDER = {
  red:   '#E11D48',
  black: '#71717A',
  gray:  '#A78BFA',
};

const RED = '#DC2626';

export default function OvalTable({
  players,
  rolesById,
  selectedSeat,
  onSelectSeat,
  voteTargets = [],
  nightTargetSeats = [],
  showRoles = true,
  currentSpeakerSeat = null,
  immunitySeats = [],       // seats currently holding day-immunity (shows a shield mark)
  showImmunityHover = false, // when true (during day), hovering a seat reveals a Grant-Immunity button
  onGrantImmunity = null,   // (seat) => void
}) {
  // eslint-disable-next-line no-unused-vars
  const { t } = useTranslation();
  const positions = useMemo(() => seatPositions(players.length), [players.length]);
  const playerCount = players.length;
  const [hoverSeat, setHoverSeat] = useState(null);

  const seatR = playerCount <= 12 ? 7 : playerCount <= 18 ? 5.6 : 4.6;
  const iconFs = playerCount <= 12 ? 7 : playerCount <= 18 ? 5.5 : 4.5;
  const numFs = playerCount <= 12 ? 3.2 : 2.6;

  return (
    <div className="relative w-full" data-testid="oval-table">
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full h-auto block">
        {/* Outer thin rim line — sharp, no glow */}
        <rect
          x={FELT_X - 2}
          y={FELT_Y - 2}
          width={FELT_W + 4}
          height={FELT_H + 4}
          rx={FELT_R + 2}
          ry={FELT_R + 2}
          fill="none"
          stroke="#1F0707"
          strokeWidth="0.8"
        />
        {/* Felt — single solid colour */}
        <rect
          x={FELT_X}
          y={FELT_Y}
          width={FELT_W}
          height={FELT_H}
          rx={FELT_R}
          ry={FELT_R}
          fill="#1A0707"
        />
        {/* Sharp inner pinstripe */}
        <rect
          x={FELT_X + 3}
          y={FELT_Y + 3}
          width={FELT_W - 6}
          height={FELT_H - 6}
          rx={FELT_R - 3}
          ry={FELT_R - 3}
          fill="none"
          stroke="#3A1010"
          strokeWidth="0.4"
        />
        {/* Crisp red accent ring */}
        <rect
          x={FELT_X + 1.2}
          y={FELT_Y + 1.2}
          width={FELT_W - 2.4}
          height={FELT_H - 2.4}
          rx={FELT_R - 1.2}
          ry={FELT_R - 1.2}
          fill="none"
          stroke={RED}
          strokeWidth="0.35"
          strokeOpacity="0.55"
        />

        {/* Centre brand mark — minimalist */}
        <g style={{ pointerEvents: 'none' }} opacity="0.18">
          <text
            x={CX}
            y={CY - 1}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="11"
            fontFamily="'Oswald', sans-serif"
            fontWeight="600"
            fill={RED}
            letterSpacing="2"
          >
            MAFIA
          </text>
          <line x1={CX - 18} y1={CY + 5} x2={CX + 18} y2={CY + 5} stroke={RED} strokeWidth="0.3" />
        </g>

        {/* Host position notch on the bottom rim */}
        <g transform={`translate(${CX} ${FELT_Y + FELT_H})`}>
          <rect x="-16" y="-1" width="32" height="2" fill={RED} opacity="0.7" />
        </g>

        {/* Seats */}
        {players.map((p, i) => {
          const pos = positions[i];
          const role = p.role_id ? rolesById[p.role_id] : null;
          const isSelected = selectedSeat === p.seat;
          const isVote = voteTargets.includes(p.seat);
          const isNightTarget = nightTargetSeats.includes(p.seat);
          const isSpeaker = currentSpeakerSeat === p.seat;
          const hasImmunity = immunitySeats.includes(p.seat);
          const isHover = hoverSeat === p.seat;
          const teamColor = role ? TEAM_BORDER[role.team] : '#52525B';
          const dead = !p.alive;
          const fillBg = dead ? '#0A0A0A' : '#0A0303';
          const ringColor = dead ? '#3F3F46' : teamColor;
          const ringWidth = isSelected ? 1.3 : 0.7;

          return (
            <g
              key={p.id}
              onClick={() => onSelectSeat && onSelectSeat(p.seat)}
              onMouseEnter={() => !dead && setHoverSeat(p.seat)}
              onMouseLeave={() => setHoverSeat((s) => (s === p.seat ? null : s))}
              data-testid={`oval-seat-${p.seat}`}
              style={{ cursor: 'pointer' }}
              opacity={dead ? 0.55 : 1}
            >
              {/* Sharp focus ring for selected/target — no glow */}
              {(isSelected || isVote || isNightTarget || isSpeaker) && (
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={seatR + 1.6}
                  fill="none"
                  stroke={isVote ? '#F43F5E' : isNightTarget ? RED : isSpeaker ? '#F59E0B' : RED}
                  strokeWidth="0.6"
                />
              )}
              <circle
                cx={pos.x}
                cy={pos.y}
                r={seatR}
                fill={fillBg}
                stroke={ringColor}
                strokeWidth={ringWidth}
              />

              {dead ? (
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={iconFs}
                  fill="#EF4444"
                >
                  ☠
                </text>
              ) : showRoles && role ? (
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={iconFs}
                >
                  {role.icon}
                </text>
              ) : (
                /* No role assigned and roles hidden — show the player's initials
                   instead of a seat number, so the UI stays "name-first". */
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={iconFs * 0.7}
                  fontFamily="'Oswald', sans-serif"
                  fill="#F5F5F5"
                  fontWeight="700"
                >
                  {(p.name || '').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '·'}
                </text>
              )}

              {isVote && (
                <g transform={`translate(${pos.x - seatR * 0.65}, ${pos.y - seatR * 0.65})`}>
                  <rect x="-1.5" y="-1.5" width="3" height="3" fill="#F43F5E" />
                  <text fontSize="2.1" fontWeight="700" fill="white" textAnchor="middle" dominantBaseline="central" dy="0.1">V</text>
                </g>
              )}
              {isNightTarget && !isVote && (
                <g transform={`translate(${pos.x - seatR * 0.65}, ${pos.y - seatR * 0.65})`}>
                  <rect x="-1.5" y="-1.5" width="3" height="3" fill={RED} />
                  <text fontSize="2.1" fontWeight="700" fill="black" textAnchor="middle" dominantBaseline="central" dy="0.1">×</text>
                </g>
              )}

              {/* Player name + role name — stacked outside the seat circle, away from the table center */}
              {(p.name || (showRoles && role)) && (() => {
                const dx = pos.x - CX;
                const dy = pos.y - CY;
                const len = Math.sqrt(dx * dx + dy * dy) || 1;
                const nx = dx / len;
                const ny = dy / len;
                const baseOffset = seatR + 2.2;
                const ox = pos.x + nx * baseOffset;
                const oy = pos.y + ny * baseOffset;
                const display = p.name && p.name.length > 14 ? p.name.slice(0, 13) + '…' : (p.name || '');
                const roleName = showRoles && role ? role.name : '';
                const roleDisplay = roleName.length > 16 ? roleName.slice(0, 15) + '…' : roleName;
                // Stack vertically: name on top, role below (further from center)
                const lineGap = 3.0;
                const roleOx = pos.x + nx * (baseOffset + lineGap);
                const roleOy = pos.y + ny * (baseOffset + lineGap);
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    {display && (
                      <text
                        x={ox}
                        y={oy}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="2.6"
                        fontFamily="'Inter', sans-serif"
                        fontWeight="600"
                        fill={dead ? '#6B7280' : '#F5EBE7'}
                        style={{ paintOrder: 'stroke', stroke: '#050505', strokeWidth: 0.4, strokeLinejoin: 'round' }}
                      >
                        {display}
                      </text>
                    )}
                    {roleDisplay && (
                      <text
                        x={roleOx}
                        y={roleOy}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="2.2"
                        fontFamily="'Inter', sans-serif"
                        fontWeight="500"
                        fill={dead ? '#52525B' : teamColor}
                        style={{ paintOrder: 'stroke', stroke: '#050505', strokeWidth: 0.4, strokeLinejoin: 'round' }}
                      >
                        {roleDisplay}
                      </text>
                    )}
                  </g>
                );
              })()}

              {/* Persistent immunity shield mark (top-right of seat) */}
              {hasImmunity && (
                <g style={{ pointerEvents: 'none' }}>
                  <circle
                    cx={pos.x + seatR * 0.78}
                    cy={pos.y - seatR * 0.78}
                    r={seatR * 0.38}
                    fill="#0A0A0A"
                    stroke="#FCD34D"
                    strokeWidth={0.4}
                  />
                  <text
                    x={pos.x + seatR * 0.78}
                    y={pos.y - seatR * 0.78}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={seatR * 0.55}
                    fill="#FCD34D"
                  >
                    ⛨
                  </text>
                </g>
              )}

              {/* Hover button — grant day immunity (only during day when prop is on) */}
              {showImmunityHover && isHover && !dead && onGrantImmunity && (
                <g
                  onClick={(e) => {
                    e.stopPropagation();
                    onGrantImmunity(p.seat);
                  }}
                  style={{ cursor: 'pointer' }}
                  data-testid={`grant-immunity-${p.seat}`}
                >
                  <circle
                    cx={pos.x}
                    cy={pos.y - seatR - 3}
                    r={2.4}
                    fill="#F59E0B"
                    stroke="#FCD34D"
                    strokeWidth={0.4}
                  />
                  <text
                    x={pos.x}
                    y={pos.y - seatR - 3}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="3"
                    fill="#0A0505"
                    fontWeight="700"
                  >
                    ⛨
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {/* Host label — clean rectangle, no glass effect */}
      <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: '3%' }}>
        <div
          className="px-5 py-2 text-[10px] uppercase tracking-[0.4em] font-semibold border flex items-center gap-2 bg-[#050505]"
          style={{ color: '#F5F5F5', borderColor: RED }}
          data-testid="oval-host-badge"
        >
          <Skull size={11} style={{ color: RED }} />
          Host
        </div>
      </div>
    </div>
  );
}
